import { useDeferredValue, useMemo } from 'react';
import {
  allPages, collectIssues, layoutPage, pageGeometry, prepareOccurrences, type Issue, type PageLayout, type ProjectDoc,
} from '@cal/engine';
import { browserFonts, useFonts } from '../fonts';
import { useAssets } from '../assets';

function fontIdsOf(doc: ProjectDoc): string[] {
  const ids = new Set<string>(['inter-700']);
  const walk = (v: unknown) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') for (const [k, x] of Object.entries(v)) { if (k === 'fontId' && typeof x === 'string') ids.add(x); else walk(x); }
  };
  walk(doc.designs);
  walk(doc.overrides);
  return [...ids].sort();
}
function assetIdsOf(doc: ProjectDoc): string[] {
  const ids = new Set<string>(doc.photoPool);
  for (const p of Object.values(doc.photos)) for (const x of Object.values(p)) ids.add(x.assetId);
  if (doc.brand.logoAssetId) ids.add(doc.brand.logoAssetId);
  return [...ids].sort();
}

/** Resources (fonts, asset metadata) needed to lay out a document. */
export function useDocResources(doc: ProjectDoc) {
  const fontIds = useMemo(() => fontIdsOf(doc), [doc.designs, doc.overrides]); // eslint-disable-line react-hooks/exhaustive-deps
  const assetIds = useMemo(() => assetIdsOf(doc), [doc.photoPool, doc.photos, doc.brand.logoAssetId]); // eslint-disable-line react-hooks/exhaustive-deps
  const fonts = useFonts(fontIds);
  const assets = useAssets(assetIds);
  return { fontVersion: fonts.version, fontsLoading: fonts.loading, assets: assets.map, assetVersion: assets.version, assetsLoading: assets.loading };
}

/** Synchronous layout of one page (used for the large preview — no server round trip). */
export function usePageLayout(doc: ProjectDoc, pageKey: string, res: ReturnType<typeof useDocResources>): PageLayout {
  const occ = useMemo(() => prepareOccurrences(doc), [doc.eventSets, doc.year]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(
    () => layoutPage(doc, pageKey, { fonts: browserFonts, assets: res.assets, occurrences: occ }),
    [doc, pageKey, res.fontVersion, res.assetVersion, occ], // eslint-disable-line react-hooks/exhaustive-deps
  );
}

/**
 * Layout of every page, computed from a deferred copy of the document so typing
 * and style changes stay responsive while thumbnails catch up.
 */
export function useAllLayouts(doc: ProjectDoc, res: ReturnType<typeof useDocResources>): { layouts: Map<string, PageLayout>; issues: Issue[]; stale: boolean } {
  const deferred = useDeferredValue(doc);
  const occ = useMemo(() => prepareOccurrences(deferred), [deferred.eventSets, deferred.year]); // eslint-disable-line react-hooks/exhaustive-deps
  const result = useMemo(() => {
    const layouts = new Map<string, PageLayout>();
    for (const p of allPages(deferred)) layouts.set(p.key, layoutPage(deferred, p.key, { fonts: browserFonts, assets: res.assets, occurrences: occ }));
    return { layouts, issues: collectIssues(deferred, [...layouts.values()], browserFonts) };
  }, [deferred, res.fontVersion, res.assetVersion, occ]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...result, stale: deferred !== doc };
}

/** Page geometry per page key (tent faces and the carrier card differ from the leaves). */
export function useGeometry(doc: ProjectDoc) {
  return useMemo(() => {
    const cache = new Map<string, ReturnType<typeof pageGeometry>>();
    return (key: string) => {
      let g = cache.get(key);
      if (!g) cache.set(key, (g = pageGeometry(doc, key)));
      return g;
    };
  }, [doc.product]); // eslint-disable-line react-hooks/exhaustive-deps
}
